/**
 * Where a scanned label lands.
 *
 * A QR sticker encodes `/inventory/scan/HH-INV-0042`, so pointing any phone camera at
 * it opens this route. It resolves the code to an item and redirects — the label does
 * not need to know the item's UUID, which is the whole reason the code exists.
 *
 * Kept as its own route rather than folded into the detail page: the URL on a sticker
 * outlives the app, and it should stay a stable, meaningful address rather than one
 * that happens to work because of how the router is wired today.
 *
 * When the portal becomes a PWA this is also where the camera scanner will send its
 * result — the resolution step is already server-side, so nothing here changes.
 */

import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Card, ErrorNotice, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';

export function InventoryScanPage() {
  const { t } = useI18n();
  const { code = '' } = useParams<{ code: string }>();

  const result = useQuery({
    queryKey: ['inventory-scan', code],
    queryFn: () => api.scanInventory(code),
    // A mistyped or retired code is a 404, and retrying it three times just makes the
    // person wait longer to be told so.
    retry: false,
  });

  useEffect(() => {
    document.title = `${code} · HH Electrical Systems`;
  }, [code]);

  if (result.isLoading) return <Spinner label={t('inventory.resolving')} />;

  if (result.error) {
    return (
      <Card className="mt-6">
        <h1 className="font-bold mb-1">{t('inventory.scanFailed')}</h1>
        <p className="text-sm text-muted mb-4">{t('inventory.scanFailedHint', { code })}</p>
        <ErrorNotice error={result.error} />
      </Card>
    );
  }

  // `replace` so the scan URL does not sit in the history: pressing back should leave
  // the item, not resolve the same code again.
  return <Navigate to={`/inventory/${result.data!.item.id}`} replace />;
}
